import { useEffect, useRef } from 'react';
import { IPHEPlateType } from '../../../Models/IPHEPlateType';
import { AvailableProductLine } from '../../../Models/Enums';
import { 
  usePHEPlateTypeManagementState 
} from './PHEPlateTypeManagementTypes';
import { getUnitConfigurationKeyAsDashedString } from '../../../Utils/UnitConfigurationHelper';
import { IUnitConfiguration } from '../../../Models/IUnitConfiguration';

/**
 * Event handlers factory for PHEPlateTypeManagement component
 */
export const createPHEPlateTypeEventHandlers = (
  state: ReturnType<typeof usePHEPlateTypeManagementState>,
  apiOperations: {
    getAllUnitConfigs: () => Promise<void>;
    getAllPHETypes: () => Promise<void>;
    getPHETypeAvailability: (key: string) => Promise<{ disabledPositions: string[]; availablePositions: string[]; }>;
    deletePheType: (item: IPHEPlateType) => Promise<void>;
    savePhePlateType: (
      pheTypeActive: IPHEPlateType | undefined,
      text: string,
      key: string,
      availableInALICE: boolean,
      infoForALICE: string,
      industrial: boolean,
      hygienic: boolean,
      selectedPositions: string[]
    ) => Promise<void>;
  }
) => {

  /**
   * Get cached PHE type availability data from already loaded data
   */
  const getCachedPHETypeAvailability = (key: string) => {
    const itemIndex = state.currentData.findIndex(item => item.key === key);
    if (itemIndex === -1 || !state.disabledForPositions[itemIndex]) {
      return null;
    }

    const disData = state.disabledForPositions[itemIndex];
    let disAllowedpos: string[] = [];
    let allowedPos: string[] = [];
    
    if (disData.length > 0) {
      for (let i = 0; i < disData.length; i++) {
        const element = disData[i];
        let config: IUnitConfiguration = {
          industry: element.industry,
          subIndustry: element.subIndustry,
          application: element.application,
          subApplication: element.subApplication,
          productLine: element.productLine,
          processStage: element.processStage,
          alfaLavalPosition: element.alfaLavalPosition,
          hotMediaType: '',
          coldMediaType: '',
          breakdownOfProcessStage: '',
          inputs: [],
          nonUsedProperties: [],
          flowType: '',
          outputs: [],
          prefferedUnit: '',
          samplingId: ''
        };
        
        const configString: string = getUnitConfigurationKeyAsDashedString(config);
        if (!element.isAvailable) {
          disAllowedpos.push(configString);
        } else {
          allowedPos.push(configString);
        }
      }
    }

    return { disabledPositions: disAllowedpos, availablePositions: allowedPos };
  };

  /**
   * Edit PHE type - load data and show modal (OPTIMIZED VERSION)
   */
  const editPHEtype = async (item: IPHEPlateType) => {
    state.setActivePHEType(item);
    state.setText(item.text);
    state.setKey(item.key);
    state.setInfoForALICE(item.informationForUser || '');
    state.setAvailableInALICE(item.isAvailableInALICE);
    
    try {
      // Try to use cached data first
      const cachedAvailabilityData = getCachedPHETypeAvailability(item.key);
      
      if (cachedAvailabilityData) {
        // Use cached data - no API call needed!
        state.setSelectedPositions(cachedAvailabilityData.disabledPositions);
        state.setAvailablePositions(cachedAvailabilityData.availablePositions);
        state.setAllowedTexts(cachedAvailabilityData.availablePositions);
      } else {
        // Fall back to API call if cached data is not available
        const availabilityData = await apiOperations.getPHETypeAvailability(item.key);
        state.setSelectedPositions(availabilityData.disabledPositions);
        state.setAvailablePositions(availabilityData.availablePositions);
        state.setAllowedTexts(availabilityData.availablePositions);
      }

      state.setIndustrial(item.availableProductLine === AvailableProductLine.Industrial);
      state.setHygienic(item.availableProductLine === AvailableProductLine.Hygienic);

      state.openModal();
    } catch (error) {
      console.error('Failed to load PHE type availability data:', error);
    }
  };

  /**
   * Handle form submission (add/update PHE type)
   */
  const handleFormSubmit = async () => {
    state.setIsSaving(true);
    try {
      await apiOperations.savePhePlateType(
        state.pheTypeActive,
        state.text,
        state.key,
        state.availableInALICE,
        state.infoForALICE,
        state.industrial,
        state.hygienic,
        state.selectedPositions
      );
      
      state.resetModalState();
    } catch (error) {
      console.error('Failed to save PHE plate type:', error);
      state.setIsSaving(false);
    }
  };

  /**
   * Handle delete confirmation
   */
  const handleDeleteConfirm = async (item: IPHEPlateType) => {
    try {
      await apiOperations.deletePheType(item);
    } catch (error) {
      console.error('Failed to delete PHE plate type:', error);
    }
  };

  /**
   * Handle search field changes
   */
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    state.setSearchTerm(e.target.value);
  };

  /**
   * Clear search field
   */
  const handleClearSearch = () => {
    state.setSearchTerm('');
  };

  /**
   * Handle sort request
   */
  const handleRequestSort = (property: string) => {
    const column = state.dataColumns.find(col => col.id === property);
    if (!column || column.sortable === false) return;
    
    const isAsc = state.orderBy === property && state.order === 'asc';
    state.setOrder(isAsc ? 'desc' : 'asc');
    state.setOrderBy(property);
  };

  /**
   * Handle delete button click
   */
  const handleDeleteClick = (item: IPHEPlateType) => {
    state.setShowDeleteConfirmation(item.key);
  };

  /**
   * Handle cancel delete
   */
  const handleDeleteCancel = () => {
    state.setShowDeleteConfirmation('');
  };

  /**
   * Handle modal close
   */
  const handleModalClose = () => {
    state.resetModalState();
  };

  /**
   * Handle create new button click
   */
  const handleCreateNew = () => {
    state.openModal();
  };

  /**
   * Handle text field change
   */
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    state.setText(e.target.value);
  };

  /**
   * Handle key field change
   */
  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    state.setKey(e.target.value);
  };

  /**
   * Handle info for ALICE field change
   */
  const handleInfoForALICEChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    state.setInfoForALICE(e.target.value);
  };

  /**
   * Handle available in ALICE checkbox change
   */
  const handleAvailableInALICEChange = () => {
    state.setAvailableInALICE(prev => !prev);
  };

  return {
    editPHEtype,
    handleFormSubmit,
    handleDeleteConfirm,
    handleSearchChange,
    handleClearSearch,
    handleRequestSort,
    handleDeleteClick,
    handleDeleteCancel,
    handleModalClose,
    handleCreateNew,
    handleTextChange,
    handleKeyChange,
    handleInfoForALICEChange,
    handleAvailableInALICEChange
  };
};

/**
 * Hook for component initialization and lifecycle
 */
export const usePHEPlateTypeInitialization = (
  apiOperations: {
    getAllUnitConfigs: () => Promise<void>;
    getAllPHETypes: () => Promise<void>;
  }
) => {
  // Load data on component mount only - use useRef to ensure we only call this once
  const hasInitialized = useRef(false);
  
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      
      const fetchData = async () => {
        try {
          // Run both operations in parallel and only complete when BOTH are done
          await Promise.all([
            apiOperations.getAllPHETypes(),
            apiOperations.getAllUnitConfigs()
          ]);
          console.log('PHE Plate Type Management initialization complete');
        } catch (error) {
          console.error('Failed to initialize PHE Plate Type Management data:', error);
        }
      };
      
      fetchData();
    }
  }, []); // Empty dependency array - run only once on mount
}; 